/**
 * New tasks and things the user has opened (Part 09 decision 5): their
 * wall cards show until then. Kept per account on this device as
 * `{ "task:<id>" | "thing:<id>": openedAt }`; entries past the cards' 7
 * days are dropped on every write. Written where every open passes
 * (`openTaskDetails`, `openMemoryNode` with a thing), so an open from the
 * board, a notification or the hub clears the card too; other windows
 * follow through the `storage` event.
 */
import { useSyncExternalStore } from "react";
import { accountScopedStorage } from "@/lib/account-scoped-storage";

/** How long a new task or thing shows on the wall: 7 days. */
export const WALL_NEW_MS = 7 * 24 * 60 * 60 * 1000;

export type WallNewKey = `task:${string}` | `thing:${string}`;

const storage = accountScopedStorage("denker.wall-new-opened.v1:");
const NONE: ReadonlyMap<string, number> = new Map();

let snapshot: { raw: string | null; opened: ReadonlyMap<string, number> } = {
  raw: null,
  opened: NONE,
};

function parse(raw: string | null): ReadonlyMap<string, number> {
  if (!raw) return NONE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return NONE;
    }
    return new Map(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] => typeof entry[1] === "number",
      ),
    );
  } catch {
    return NONE;
  }
}

/** Opened keys and when; the same map until the stored value changes. */
function readOpened(): ReadonlyMap<string, number> {
  const raw = storage.read();
  if (raw !== snapshot.raw) snapshot = { raw, opened: parse(raw) };
  return snapshot.opened;
}

export function markWallItemOpened(
  key: WallNewKey,
  now: number = Date.now(),
): void {
  const kept: Record<string, number> = {};
  for (const [opened, at] of readOpened()) {
    if (now - at < WALL_NEW_MS) kept[opened] = at;
  }
  kept[key] = now;
  storage.write(JSON.stringify(kept));
}

/** The opened keys, live across this window's writes and other windows'. */
export function useWallOpenedKeys(): ReadonlyMap<string, number> {
  return useSyncExternalStore(storage.subscribe, readOpened, () => NONE);
}
